import React, { FC, useMemo } from 'react'
import { Box, MenuItem } from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import { useFormik } from 'formik'
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date'
import Button from 'UI/Button/ButtonWithPreloader'
import SelectComponent from 'UI/Select'
import RequiredOption from 'UI/Select/RequiredOption'
import DecimalTextField from 'UI/TextField/DecimalTextField'
import convertDateForPicker from 'utils/convertDateForPicker'
import { IBankAccountClient } from 'typescript/interfaces/clients'
import { ISendAchAuthorizationFormPayload } from 'services/deals'
import DatePickerWithTooltip from 'components/DatePickerWithTooltip'
import { schemaSendAchAuthorizationForm } from './schema'

interface ISendAchAuthorizationFormValues {
  bankAccountId: string | number | ''
  amountOfTransaction: string
  achEffectiveDate: Date | null
}

interface ISendAchAuthorizationFormProps {
  defaultAmount?: string,
  defaultBankAccountId?: number | string | null,
  defaultAchEffectiveDate?: string | null,
  bankAccounts?: IBankAccountClient[]
  isLoading?: boolean
  cancel: () => void
  submit: (payload: ISendAchAuthorizationFormPayload) => void
}

export const SendAchAuthorizationForm: FC<ISendAchAuthorizationFormProps> = ({
  bankAccounts = [],
  defaultAmount = '',
  defaultBankAccountId = null,
  defaultAchEffectiveDate = null,
  isLoading,
  cancel,
  submit,
}) => {

  const initialValues = useMemo<ISendAchAuthorizationFormValues>(() => {
    const requiredBankAccountId = defaultBankAccountId ?? bankAccounts[0]?.bankAccount?.id ?? ''

    return {
      bankAccountId: requiredBankAccountId !== '' ? Number(requiredBankAccountId) : '',
      amountOfTransaction: defaultAmount,
      achEffectiveDate: defaultAchEffectiveDate ? new Date(defaultAchEffectiveDate) : null,
    }
  }, [defaultBankAccountId, bankAccounts, defaultAmount, defaultAchEffectiveDate])

  const { handleSubmit, handleChange, values, errors, touched, setFieldValue } = useFormik<
    ISendAchAuthorizationFormValues
  >({
    initialValues,
    validationSchema: schemaSendAchAuthorizationForm,
    enableReinitialize: true,
    onSubmit: (vals) => {
      submit({
        bankAccountId: Number(vals.bankAccountId),
        amountOfTransaction: Number(vals.amountOfTransaction),
        achEffectiveDate: (vals.achEffectiveDate as Date).toISOString(),
      })
    },
  })

  return (
    <Box width={360} maxWidth="100%">
      <form onSubmit={handleSubmit}>
        <Box>
          <Typography variant="h2" color="textSecondary">
            ACH Authorization Form
          </Typography>
        </Box>
         <Box mt="2rem">
          <SelectComponent
            value={values.bankAccountId}
            name="bankAccountId"
            label={<RequiredOption label="Bank Account" />}
            onChange={(e) => setFieldValue('bankAccountId', e.target.value as number)}
            error={Boolean(touched.bankAccountId && errors.bankAccountId)}
            helperText={touched.bankAccountId && (errors.bankAccountId as string)}
          >
            {bankAccounts.map((b) => {
              const ba = b.bankAccount
              return (
                <MenuItem key={ba?.id} value={ba?.id as number}>
                  {ba?.bankAccount || `Account #${ba?.id}`}
                </MenuItem>
              )
            })}
          </SelectComponent>
        </Box>
        <Box mt="2rem">
          <DecimalTextField
            label={<RequiredOption label="Amount of Transaction" />}
            variant="outlined"
            fullWidth
            name="amountOfTransaction"
            onChange={handleChange}
            value={values.amountOfTransaction}
            error={Boolean(touched.amountOfTransaction && errors.amountOfTransaction)}
            helperText={touched.amountOfTransaction && errors.amountOfTransaction}
          />
        </Box>
        <Box mt="2rem">
          <DatePickerWithTooltip
            fullWidth
            type="keyboard"
            label={<RequiredOption label="Effective ACH Authorization Date" />}
            InputLabelProps={{ shrink: true }}
            name="achEffectiveDate"
            value={values.achEffectiveDate}
            onChange={(value: MaterialUiPickersDate) =>
              setFieldValue('achEffectiveDate', convertDateForPicker(value))
            }
            error={Boolean(touched.achEffectiveDate && errors.achEffectiveDate)}
            helperText={touched.achEffectiveDate && (errors.achEffectiveDate as string)}
            tooltipText="The date from which the ACH Authorization Form becomes effective and authorizes ACH payments for the deal."
          />
        </Box>

        <Box mt="2rem" display="flex" justifyContent="flex-end">
          <Button color="secondary" variant="contained" onClick={() => cancel()}>
            Cancel
          </Button>
          <Box ml="2rem">
            <Button loading={isLoading} type="submit" color="primary" variant="contained">
              Send ACH Authorisation Form
            </Button>
          </Box>
        </Box>
      </form>
    </Box>
  )
}